import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Handshake, Target } from "lucide-react";
import api from "../lib/api";
import { DEFAULT_ABOUT } from "../lib/homeContent";

const valueIcons = [ShieldCheck, Users, Handshake, Target];

const Section = ({ children, className = "" }) => (
  <section className={`mx-auto max-w-7xl px-6 lg:px-10 ${className}`}>{children}</section>
);

export default function WhoWeAre() {
  const [c, setC] = useState(DEFAULT_ABOUT);
  useEffect(() => {
    api.get("/content/about").then(({ data }) => setC({ ...DEFAULT_ABOUT, ...data })).catch(() => {});
  }, []);

  const values = [1, 2, 3, 4].map((i) => ({
    icon: valueIcons[i - 1],
    title: c[`value_${i}_title`],
    body: c[`value_${i}_body`],
  }));

  return (
    <div data-testid="who-we-are-page">
      {/* Page hero */}
      <section className="relative pt-40 pb-24 bg-sp-dark sp-grain overflow-hidden">
        {c.hero_image && <img src={c.hero_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-t from-sp-dark to-sp-dark/50" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="sp-eyebrow text-sky-300/80">
            {c.hero_eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6 font-serif text-4xl md:text-6xl text-white max-w-4xl leading-[1.08]"
          >
            {c.hero_title}
          </motion.h1>
        </div>
      </section>

      {/* Narrative */}
      <Section className="py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="sp-eyebrow">{c.story_eyebrow}</p>
            <h2 className="mt-5 font-serif text-3xl md:text-4xl text-sp-deep leading-tight">
              {c.story_heading}
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 font-sans text-lg leading-relaxed text-sp-soft">
            <p>{c.story_p1}</p>
            <p>{c.story_p2}</p>
            <p>{c.story_p3}</p>
          </div>
        </div>
      </Section>

      {/* Values */}
      <section className="bg-white border-y border-sp-navy/10">
        <Section className="py-24">
          <p className="sp-eyebrow">{c.values_eyebrow}</p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl text-sp-deep">{c.values_heading}</h2>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-px bg-sp-navy/10 border border-sp-navy/10">
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`value-${i}`}
                className="bg-white p-10 hover:bg-sp-bg transition-colors duration-300"
              >
                <v.icon className="w-7 h-7 text-sp-navy" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl text-sp-deep">{v.title}</h3>
                <p className="mt-3 font-sans text-base leading-relaxed text-sp-soft">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      {/* CTA */}
      <Section className="py-28 text-center">
        <h2 className="font-serif text-3xl md:text-5xl text-sp-deep max-w-3xl mx-auto leading-[1.15]">
          {c.cta_heading}
        </h2>
        <Link
          to="/directors"
          data-testid="who-directors-btn"
          className="mt-10 inline-flex items-center gap-2 rounded-full bg-sp-navy px-9 py-4 text-sm font-medium text-white hover:bg-sp-deep transition-colors duration-300"
        >
          {c.cta_button}
        </Link>
      </Section>
    </div>
  );
}
